import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Projectile } from '../entities/projectile';
import { WA_PHYSICS } from '../core/constants';
import { computeSafeSpawnPosition } from './spawnHelper';

/**
 * Trico T2 (GunBound mobile - Thor's Hammer). Fires 3 lettuce balls that each
 * explode on contact. All three share the SAME launch velocity (one ballistic
 * center); the two outer balls orbit around that center in flight (their
 * landing spread depends on the spin phase at impact). The orbit is applied in
 * Projectile.update via `orbitDir`/`orbitAngle` (the offset is baked into the
 * synced position, so online clients render it for free).
 */
export const WeaponTrico: WeaponDefinition = {
  id: 'trico',
  name: 'Trico',
  icon: 'trico.png',
  category: 'thrown',
  maxAmmo: 2,
  maxUsesPerTurn: 1,
  powerCharging: true,
  timerAdjustable: false,
  showAimGuide: true,
  allowRetreat: true,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle, power, world } = ctx;

    const speed = (power / 100) * 750;
    const rad = (angle * Math.PI) / 180;
    const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;
    const spawn = computeSafeSpawnPosition(worm, aimRad, 20, world);
    const vx = Math.cos(aimRad) * speed;
    const vy = -Math.sin(aimRad) * speed;

    const make = (orbitDir: number, orbitAngle: number): Projectile =>
      new Projectile({
        x: spawn.x,
        y: spawn.y,
        vx,
        vy,
        type: 'trico',
        timer: -1,
        windAffected: true,
        explosionRadius: WA_PHYSICS.TRICO.EXPLOSION_RADIUS,
        maxDamage: WA_PHYSICS.TRICO.MAX_DAMAGE,
        ownerId: worm.id,
        orbitDir,
        orbitAngle,
      });

    // Center ball (no orbit) + two outer balls orbiting on opposite phases.
    return {
      success: true,
      entities: [make(0, 0), make(1, 0), make(1, Math.PI)],
    };
  },
};
